<template>
  <td>
    <div
      v-if="!isSentimentChart"
      :class="{ cell__tooltip: data.info }"
      :style="data.style"
      :tabindex="data.info ? '0' : null"
      v-on="data.info ? { click: toggleInfo, keydown: toggleInfo } : {}"
    >
      <span v-html="cellData"></span>
      <template v-if="data.info">
        <p ref="tooltip-text" :class="['cell__tooltip-text', { open: isShowingInfo }]">
          {{ data.info }}
        </p>
      </template>
    </div>
    <div v-else>
      <PositiveNegativeBarChartSmall v-if="PosNegData" :value="PosNegData">
      </PositiveNegativeBarChartSmall>
    </div>
  </td>
</template>

<script>
import PositiveNegativeBarChartSmall from '../PositiveNegativeBarChartSmall/DisplayPositiveNegativeBarChartSmall';
import PigeonDocNodeModel from '../../models/PigeonDocNodeModel';

export default {
  name: 'DisplayTableCell',
  components: {
    PositiveNegativeBarChartSmall,
  },
  props: {
    data: {
      default: () => {},
      type: Object,
    },
    uuid: {
      default: () => '',
      type: String,
    },
  },
  data() {
    return {
      isShowingInfo: false,
      PosNegData: null,
    };
  },
  computed: {
    cellData() {
      return this.data && this.data.value ? this.data.value : this.data;
    },
    isSentimentChart() {
      if (this.data.respondent_sentiment) {
        return true;
      }
      return false;
    },
  },
  created() {
    if (this.data.respondent_sentiment) {
      this.PosNegData = this.generatePosNegData(this.data.respondent_sentiment);
    }
  },
  methods: {
    toggleInfo($e) {
      // close tooltips for all other cells, this prevents overlapping tooltips
      const opened = document
        .getElementById(`table-${this.uuid}`)
        .querySelectorAll('.cell__tooltip-text.open');
      opened.forEach((item) => {
        if (item.isEqualNode(this.$refs['tooltip-text'])) return;
        item.classList.remove('open');
      });

      // force closed by another cell, so reset variable to false
      if (this.isShowingInfo && !this.$refs['tooltip-text'].classList.contains('open'))
        this.isShowingInfo = false;

      this.$nextTick(() => {
        if ($e.type === 'click') this.isShowingInfo = !this.isShowingInfo;
        if ($e.type === 'keydown' && $e.key === 'Enter') this.isShowingInfo = !this.isShowingInfo;
        if ($e.type === 'keydown' && ($e.key === 'Esc' || $e.key === 'Escape'))
          this.isShowingInfo = false;
      });
    },
    generatePosNegData: function (data) {
      if (!data || !Array.isArray(data)) data = [];

      const totals = this.calcRespondentSentimentTotals(data);
      const description =
        `The chart shows sentiments for each respondent in this theme. ` +
        `It shows ${this.roundToTwo(totals.neg)} ` +
        `responses have a negative sentiment, ${this.roundToTwo(totals.neu)} ` +
        `are netural, and ${this.roundToTwo(totals.pos)} are positive.`;

      return new PigeonDocNodeModel({
        type: 'positiveNegativeBarChartSmall',
        content: {
          data: data,
          meta: {
            veryPositiveColour: 'var(--very-positive-soft)',
            positiveColour: 'var(--somewhat-positive-soft)',
            neutralColour: 'var(--neutral-soft)',
            negativeColour: 'var(--somewhat-negative-soft)',
            veryNegativeColour: 'var(--very-negative-soft)',
          },
        },
        meta: {
          description: description,
        },
      });
    },
    calcRespondentSentimentTotals(data) {
      return data.reduce(
        (totals, data) => {
          if (data > -0.05 && data < 0.05) totals.neu++;
          if (data >= 0.05) totals.pos++;
          if (data <= -0.05) totals.neg++;
          return totals;
        },
        { neu: 0, pos: 0, neg: 0 },
      );
    },
    roundToTwo(num) {
      return +(Math.round(num + 'e+2') + 'e-2');
    },
  },
};
</script>

<style scoped>
.cell__tooltip {
  position: relative;
  cursor: pointer;
}

.cell__tooltip-text {
  align-items: center;
  background: hsl(0, 0%, 35%);
  border-radius: 4px;
  display: none;
  font-size: 0.9rem;
  padding: 0.2rem 0.4rem;
  color: #fff;
  width: max-content;
  max-width: 20ch;
  left: 50%;
  line-height: 1.5;
  transform: translateX(-50%);
  transition: opacity 250ms ease-out;
  position: absolute;
  z-index: 100;
}

.cell__tooltip-text::after {
  content: ' ';
  position: absolute;
  bottom: 100%;
  right: 50%;
  border-width: 12px;
  border-style: solid;
  border-color: transparent transparent hsl(0, 0%, 35%) transparent;
}

.cell__tooltip-text.open {
  display: block;
}
</style>
